/**
 * Certificate utility to wait for certificates on disk, and update
 * the run-time certificate configuration.
 * @module Certificates
 */

const path = require("path");
const {
  FileWatcher
} = require("./filewatcher");
const {
  paths
} = require("./paths");

const CERT_CLIENT_CN_NAME = "QlikClient";
const HTTP_STATUS_FORBIDDEN = 403;

const certFileMap = new Map();
certFileMap.set("client.pem", "client");
certFileMap.set("client_key.pem", "clientKey");
certFileMap.set("root.pem", "root");
certFileMap.set("server.pem", "server");
certFileMap.set("server_key.pem", "serverKey");

/**
 * Default path for where certificates are found.
 */
const certPathDefault = path.resolve(paths.senseDataPath, "Repository/Exported Certificates/.Local Certificates");

/**
 * Certificates Class - exposes a getter for retrieving certificates
 */
class Certificates {

  /**
   * Creates a Certificates object that will look for QlikSense Certificates
   * in a specifiecd folder
   * @param {string} certRoot - A path to the root folder where certificates
   * are expected to be.
   */
  constructor(certRoot) {
    this.certificateWatcher = new FileWatcher(certRoot || certPathDefault, Array.from(certFileMap.keys()));
  }

  /**
  * Gets the certificates when it is safe to get them
  * @returns { Promise<Object> } A promise that resolves to an object
  containing the certificates or rejects with an error if the files couldn't be read.
  */
  get() {
    return this.certificateWatcher.get().then(certs => {
      const certificates = {};
      certFileMap.forEach((value, key) => {
        certificates[value] = certs.get(key);
      });
      return certificates;
    });
  }
}

/**
 * Validates that a request has a valid client certificate.
 *
 * @param {Object} req The express request object
 * @param {Object} res The express response object
 * @param {Function} next The express middleware `next()` function
 * @returns {null} No-op
 */
function validateRequest(req, res, next) {
  const cert = req.connection.getPeerCertificate();
  if (!req.client.authorized || !cert || cert.subject.CN !== CERT_CLIENT_CN_NAME) {
    return res.status(HTTP_STATUS_FORBIDDEN).send("Invalid certificate");
  }
  return next();
}

/**
 * Singleton object for certificates
 */
const certificates = new Certificates();

module.exports = {
  certificates,
  Certificates,
  validateRequest
};
